import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminShippingMethodCollectionItemResponse } from '../responses/admin-shipping-method-collection-item.response';
import { AdminShippingMethodDetailsResponse } from '../responses/admin-shipping-method-details.response';
import { AdminShippingMethodUpdateRequest } from '../requests/admin-shipping-method-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminShippingApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(params: {
    request: CollectionRequest;
    cacheable?: boolean;
    skipByLoading?: boolean;
  }): Observable<
    CollectionResponse<AdminShippingMethodCollectionItemResponse>
  > {
    const path = 'admin/shipping/method';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(params.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(params?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(params?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminShippingMethodCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    id: Guid;
    skipByLoading: boolean;
  }): Observable<AdminShippingMethodDetailsResponse> {
    const path = `admin/shipping/method/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: options.id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminShippingMethodDetailsResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public update(id: Guid, request: AdminShippingMethodUpdateRequest): Observable<void> {
    const path = `admin/shipping/method/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, AdminShippingMethodUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
